import { zodResolver } from '@hookform/resolvers/zod';
import {
  BarChart3,
  CalendarClock,
  ClipboardList,
  NotebookPen,
  QrCode,
  ShoppingBasket,
  Sprout,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { normalizePhone } from '@farmos/shared';
import { Button } from '@farmos/ui/button';
import { Field } from '@farmos/ui/field';
import { Input } from '@farmos/ui/input';
import { Segmented } from '@farmos/ui/segmented';
import { BrandMark } from '@/components/layout/BrandMark';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { api } from '@/lib/api';
import { env } from '@/lib/env';
import { supabase } from '@/lib/supabase';

type Mode = 'phone' | 'email';

export function LoginPage() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>(env.smsLogin ? 'phone' : 'email');
  return (
    <main className="flex min-h-dvh p-3 sm:p-5 lg:p-6">
      <div className="mx-auto flex w-full max-w-[1280px] gap-6">
        <section className="flex w-full flex-col rounded-[30px] bg-panel p-6 shadow-card sm:p-10 lg:max-w-[560px] lg:p-12">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <BrandMark className="size-9" />
              <span className="text-lg font-semibold text-brand-2">{t('app.name')}</span>
            </div>
            <LanguageSwitcher compact />
          </div>
          <div className="my-auto w-full max-w-sm animate-rise self-center py-10">
            <img
              src="/logo.png"
              alt={t('app.farm')}
              className="mb-6 h-20 w-auto rounded-[22px] bg-white p-2 shadow-card ring-1 ring-line/60"
            />
            <h1 className="text-[28px] font-semibold tracking-tight">{t('auth.welcome')}</h1>
            <p className="mb-7 mt-1.5 text-muted">{t('auth.title')}</p>
            {env.smsLogin && (
              <Segmented
                className="mb-5 w-full [&>button]:flex-1"
                label={t('auth.title')}
                value={mode}
                onChange={setMode}
                options={[
                  { value: 'phone', label: t('auth.by_phone') },
                  { value: 'email', label: t('auth.by_email') },
                ]}
              />
            )}
            {mode === 'phone' ? <PhoneLogin /> : <EmailLogin />}
            <div className="mt-8 flex gap-3 rounded-[18px] bg-tile p-3.5 text-sm text-muted">
              <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-panel text-brand">
                <QrCode className="size-[18px]" aria-hidden />
              </span>
              <p className="self-center">
                {t('auth.subtitle')} {!env.smsLogin && t('auth.worker_hint')}
              </p>
            </div>
          </div>
        </section>
        <aside
          aria-hidden
          className="hero-surface relative hidden flex-1 overflow-hidden rounded-[30px] lg:block"
        >
          <FlowLines />
          <div className="relative p-14">
            <p className="text-[40px] font-semibold leading-tight">{t('auth.tagline')}</p>
            <p className="mt-3 max-w-md text-lg text-white/80">{t('auth.tagline_sub')}</p>
          </div>
          <div className="absolute bottom-14 end-14 grid grid-cols-3 gap-3">
            {[Sprout, ShoppingBasket, CalendarClock, ClipboardList, NotebookPen, BarChart3].map(
              (Icon, i) => (
                <span
                  key={i}
                  className="grid size-20 place-items-center rounded-[24px] bg-white/[0.14] ring-1 ring-white/25"
                >
                  <Icon className="size-8" />
                </span>
              ),
            )}
          </div>
        </aside>
      </div>
    </main>
  );
}

/** Soft flowing lines, like the lavender backgrounds across the app. */
function FlowLines() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 size-full text-white"
      viewBox="0 0 800 800"
      preserveAspectRatio="none"
      aria-hidden
    >
      {Array.from({ length: 14 }, (_, i) => {
        const y = 120 + i * 42;
        return (
          <path
            key={i}
            d={`M-40 ${y} C 200 ${y - 140 + i * 8}, 420 ${y + 160 - i * 6}, 840 ${y + 30}`}
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.08 + (i % 4) * 0.03}
            strokeWidth="1.5"
          />
        );
      })}
    </svg>
  );
}

function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-[14px] bg-gap/10 px-3 py-2 text-sm text-gap">
      {message}
    </p>
  );
}

function PhoneLogin() {
  const { t } = useTranslation();
  const [phone, setPhone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const phoneForm = useForm<{ phone: string }>({
    resolver: zodResolver(
      z.object({
        phone: z.string().refine((v) => normalizePhone(v) !== null, 'errors.invalid_phone'),
      }),
    ),
  });
  const codeForm = useForm<{ code: string }>({
    resolver: zodResolver(z.object({ code: z.string().regex(/^\d{6}$/, 'errors.required') })),
  });

  if (!phone) {
    return (
      <form
        noValidate
        className="flex flex-col gap-3"
        onSubmit={phoneForm.handleSubmit(async (v) => {
          setError(null);
          const p = normalizePhone(v.phone)!;
          const { error: e } = await supabase.auth.signInWithOtp({
            phone: p,
            options: { shouldCreateUser: false },
          });
          if (e) setError(e.message);
          else setPhone(p);
        })}
      >
        <Field label={t('auth.phone')} error={phoneForm.formState.errors.phone?.message}>
          <Input
            type="tel"
            dir="ltr"
            autoComplete="tel"
            placeholder="050-000-0000"
            {...phoneForm.register('phone')}
          />
        </Field>
        <FormError message={error} />
        <Button type="submit" size="lg" disabled={phoneForm.formState.isSubmitting}>
          {t('auth.send_code')}
        </Button>
      </form>
    );
  }

  return (
    <form
      noValidate
      className="flex flex-col gap-3"
      onSubmit={codeForm.handleSubmit(async (v) => {
        setError(null);
        const { error: e } = await supabase.auth.verifyOtp({ phone, token: v.code, type: 'sms' });
        if (e) setError(e.message);
      })}
    >
      <p className="text-sm text-muted">{t('auth.code_sent', { phone: `+${phone}` })}</p>
      <Field label={t('auth.code')} error={codeForm.formState.errors.code?.message}>
        <Input
          inputMode="numeric"
          autoComplete="one-time-code"
          dir="ltr"
          maxLength={6}
          {...codeForm.register('code')}
        />
      </Field>
      <FormError message={error} />
      <Button type="submit" size="lg" disabled={codeForm.formState.isSubmitting}>
        {t('auth.verify')}
      </Button>
      <Button variant="link" onClick={() => setPhone(null)}>
        {t('auth.change_phone')}
      </Button>
    </form>
  );
}

function EmailLogin() {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const form = useForm<{ email: string; password: string }>({
    resolver: zodResolver(
      z.object({
        email: z
          .string()
          .trim()
          .refine(
            (v) => z.email().safeParse(v).success || normalizePhone(v) !== null,
            'errors.invalid_email',
          ),
        password: z.string(),
      }),
    ),
  });

  if (sent)
    return (
      <p className="rounded-[18px] bg-money/10 p-4 text-sm">
        {t('auth.magic_sent', { email: sent })}
      </p>
    );

  const sendLink = async () => {
    const ok = await form.trigger('email');
    if (!ok) return;
    const email = form.getValues('email').trim();
    // Sign-in links go by email only; a phone number needs its password.
    if (!email.includes('@')) return form.setError('email', { message: 'errors.invalid_email' });
    const { error: e } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: false, emailRedirectTo: window.location.origin },
    });
    if (e) setError(e.message);
    else setSent(email);
  };

  return (
    <form
      noValidate
      className="flex flex-col gap-3"
      onSubmit={form.handleSubmit(async (v) => {
        setError(null);
        if (!v.password) return sendLink();
        const phone = v.email.includes('@') ? null : normalizePhone(v.email);
        if (phone) {
          try {
            const tokens = await api<{ access_token: string; refresh_token: string }>(
              '/auth/phone-login',
              { phone, password: v.password },
            );
            const { error: e } = await supabase.auth.setSession(tokens);
            if (e) setError(e.message);
          } catch (e) {
            setError((e as Error).message);
          }
          return;
        }
        const { error: e } = await supabase.auth.signInWithPassword({
          email: v.email.trim(),
          password: v.password,
        });
        if (e) setError(e.message);
      })}
    >
      <Field label={t('auth.email_or_phone')} error={form.formState.errors.email?.message}>
        <Input type="text" dir="ltr" autoComplete="username" {...form.register('email')} />
      </Field>
      <Field label={t('auth.password')}>
        <Input
          type="password"
          dir="ltr"
          autoComplete="current-password"
          {...form.register('password')}
        />
      </Field>
      <FormError message={error} />
      <Button type="submit" size="lg" disabled={form.formState.isSubmitting}>
        {t('auth.sign_in')}
      </Button>
      <Button variant="link" onClick={() => void sendLink()}>
        {t('auth.magic_link')}
      </Button>
    </form>
  );
}
